// Copyright (c) 2020 by Audere
//
// Use of this source code is governed by an LGPL-3.0 license that
// can be found in the LICENSE file distributed with this file.

import "../../style/sassStyle";

import { ROUTE_DEFINITIONS, getPath } from "routes/routes";
import React, { useContext, useEffect, useState } from "react";
import {
  CircularProgress,
  Theme,
  createStyles,
  makeStyles,
} from "@material-ui/core";
import {
  storeStepTimestamp,
  subscribeToTestRun,
  useTestRunState,
} from "store/testrun";
import { useHistory, useParams } from "react-router-dom";

import { CustomerContext } from "components/App/DashboardApp";
import DeclarativeContentRenderer from "components/DeclarativeComponents/DeclarativeContentRenderer";
import DemoBanner from "./DemoBanner";
import PageContent from "../ui/PageContent";
import TestRunHeader from "./TestRunHeader";
import { TestRunParams } from "./TestRunConstants";
import { useAppDispatch } from "store";
import { useAuthState } from "../../store/auth";
import { useTestRunConfig } from "./useTestRunConfig";

const testRunStepStyle = makeStyles((theme: Theme) =>
  createStyles({
    wrapperRoot: {
      position: "absolute",
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
      display: "flex",
      flexDirection: "column",
      backgroundColor: "rgba(0, 0, 0, .5)",
    },
    root: {
      margin: "auto",
    },
    stepContent: {
      marginBottom: "1.5rem",
    },
  })
);

/**
 * Test Kit Tutorial.
 * Each step might be just static information or contain form
 * preventing going to the next step until it is completed.
 */
export default () => {
  const { step, testRunUID } = useParams<TestRunParams>();

  const { getTestRunStep } = useTestRunConfig();
  const testRunState = useTestRunState();
  const dispatch = useAppDispatch();

  const { auth } = useAuthState();
  const history = useHistory();
  const orgId = useContext(CustomerContext).id;

  const [cachedAuth, setCachedAuth] = useState(auth);

  const style = testRunStepStyle();

  const subscribed = testRunState.subscribed;
  const subscribedTestRun = testRunState.testRun?.uid;

  useEffect(() => {
    const subscribe = async () => {
      if (!auth) {
        throw Error("User UID not found");
      } else if (subscribedTestRun !== testRunUID || !subscribed) {
        await dispatch(
          subscribeToTestRun({ orgId, userUID: auth.uid, testRunUID })
        );
      }
    };
    subscribe();
  }, [auth, orgId, subscribedTestRun, dispatch, testRunUID, subscribed]);

  // Updating the current test run step and timestamp info.
  useEffect(() => {
    let isMounted = true;
    async function updateTestRunInfo() {
      if (
        !auth ||
        testRunUID === undefined ||
        step === undefined ||
        !subscribedTestRun ||
        !subscribed
      ) {
        return;
      }
      if (auth && !cachedAuth) {
        setCachedAuth(auth);
      } else if (
        cachedAuth &&
        auth.uid !== cachedAuth.uid &&
        auth.uid === "UNSET_USER_ID"
      ) {
        return;
      }
      try {
        await dispatch(
          storeStepTimestamp({
            stepName: getTestRunStep(step).name,
          })
        );
      } catch (e) {
        if (!isMounted) {
          return;
        }
        console.log("error caught");
        console.log(e);
        // TODO: (jenny) I believe this is a race condition.
        // history.push(getPath(ROUTE_DEFINITIONS.LANDING));
      }
    }
    updateTestRunInfo();
    return () => {
      isMounted = false;
    };
  }, [
    auth,
    orgId,
    cachedAuth,
    dispatch,
    getTestRunStep,
    history,
    step,
    testRunUID,
    subscribed,
    subscribedTestRun,
  ]);

  if (testRunState === undefined) {
    return (
      <div className={style.wrapperRoot}>
        <CircularProgress
          classes={{ root: style.root }}
          size={150}
          variant={"indeterminate"}
        />
      </div>
    );
  }

  const currentStepDescription = getTestRunStep(step);

  if (
    auth &&
    cachedAuth &&
    cachedAuth.uid !== auth.uid &&
    auth.uid === "UNSET_USER_ID"
  ) {
    history.push(getPath(ROUTE_DEFINITIONS.LANDING));
    return <div />;
  }

  return (
    <PageContent>
      <DemoBanner />
      <TestRunHeader />
      <div className={"container"}>
        <div className={style.stepContent}>
          {currentStepDescription.contentDescription && (
            <DeclarativeContentRenderer
              contentDescription={currentStepDescription.contentDescription}
            />
          )}
        </div>
      </div>
    </PageContent>
  );
};
